import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, ArrowLeft, Check, X, ArrowUp, ScanLine, Users, Hourglass, Clock,
  CheckCircle2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  confirmed: { label: "Confirmé",   color: "#3A4A1F", bg: "#E9EFD7", icon: CheckCircle2 },
  waitlist:  { label: "Liste attente", color: "#8B5A2B", bg: "#F4E4C9", icon: Hourglass },
  pending:   { label: "En attente", color: "#3F4B7A", bg: "#E5E5F0", icon: Clock },
  rejected:  { label: "Refusé",     color: "#8B8680", bg: "#EADDC9", icon: X },
};

export default function AdminEventParticipants() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [scanToken, setScanToken] = useState("");
  const [scanning, setScanning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: events }, { data: parts }] = await Promise.all([
        api.get("/events"),
        api.get(`/events/${id}/participants`),
      ]);
      setEvent(events.find((e) => e.id === id) || null);
      setParticipants(Array.isArray(parts) ? parts : []);
    } catch (e) {
      toast.error("Impossible de charger les participants");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const action = async (pid, kind) => {
    try {
      await api.post(`/events/${id}/participants/${pid}/${kind}`);
      const okMsg = { approve: "Participant approuvé", reject: "Participant refusé", promote: "Participant promu en confirmé" };
      toast.success(okMsg[kind] || "Action effectuée");
      refresh();
    } catch (e) {
      toast.error(e?.userMessage || "Échec");
    }
  };

  const checkIn = async () => {
    if (!scanToken.trim()) return;
    setScanning(true);
    try {
      const { data } = await api.post(`/admin/events/${id}/check-in`, { token: scanToken.trim() });
      if (data.already_checked_in) {
        toast.info("Déjà enregistré à l'entrée");
      } else {
        toast.success("Check-in réussi");
      }
      setScanToken("");
      refresh();
    } catch (e) {
      toast.error(e?.userMessage || "Billet invalide");
    } finally { setScanning(false); }
  };

  const filtered = filter === "all" ? participants : participants.filter((p) => p.status === filter);
  const counts = participants.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {});

  return (
    <div data-testid="admin-event-participants-page">
      <button
        onClick={() => navigate("/admin/events")}
        className="inline-flex items-center gap-1.5 text-sm text-[#6E4B2A] hover:text-[#1A1A1A] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Retour aux événements
      </button>

      <div className="mb-8">
        <p className="caption-label text-[#A26F3E]">Modération · événement</p>
        <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1 leading-tight">
          {event?.title || <span className="text-[#8B8680]">Chargement…</span>}
        </h1>
        {event && (
          <p className="text-sm text-[#666] mt-2">
            {event.visibility === "private" && "Privé · sur invitation · "}
            {event.visibility === "premium" && "Premium uniquement · "}
            {event.capacity ? `Capacité ${event.capacity} · ` : "Capacité illimitée · "}
            {event.requires_approval ? "Approbation manuelle requise" : "Approbation automatique"}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Confirmés"     value={counts.confirmed || 0} accent="#3A4A1F" />
        <StatCard label="Liste attente" value={counts.waitlist || 0}  accent="#8B5A2B" />
        <StatCard label="En attente"    value={counts.pending || 0}   accent="#3F4B7A" />
        <StatCard label="Refusés"       value={counts.rejected || 0}  accent="#8B8680" />
      </div>

      {/* Check-in scanner */}
      <div className="card-lux p-5 mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end" data-testid="checkin-card">
        <div className="flex-1">
          <p className="caption-label">Check-in à l'entrée</p>
          <p className="text-xs text-[#666] mt-1 mb-3">Scannez le QR ou collez le token du billet pour enregistrer la présence.</p>
          <Input
            placeholder="Token du billet (uuid.signature)"
            value={scanToken}
            onChange={(e) => setScanToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && checkIn()}
            className="rounded-full font-mono text-xs"
            data-testid="checkin-token-input"
          />
        </div>
        <Button
          onClick={checkIn}
          disabled={scanning || !scanToken.trim()}
          className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white"
          data-testid="checkin-submit"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
          Valider l'entrée
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="participants-filters">
        <FilterTab id="all" label={`Tous (${participants.length})`} active={filter === "all"} onClick={() => setFilter("all")} />
        {["pending", "confirmed", "waitlist", "rejected"].map((k) => (
          <FilterTab key={k} id={k} label={`${STATUS_META[k].label} (${counts[k] || 0})`} active={filter === k} onClick={() => setFilter(k)} />
        ))}
      </div>

      {/* Participants list */}
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-lux p-12 text-center" data-testid="participants-empty">
          <Users className="w-8 h-8 text-[#C68E59] mx-auto" />
          <p className="font-serif text-xl text-[#1A1A1A] mt-3">Aucun participant dans cette catégorie</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="participants-list">
          {filtered.map((p) => (
            <ParticipantRow
              key={p.id}
              p={p}
              onApprove={() => action(p.id, "approve")}
              onReject={() => action(p.id, "reject")}
              onPromote={() => action(p.id, "promote")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="card-lux p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="font-serif text-3xl leading-none" style={{ color: accent }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-[#8B8680] mt-2">{label}</p>
    </div>
  );
}

function FilterTab({ id, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
        active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#EADDC9] hover:border-[#1A1A1A]"
      }`}
      data-testid={`filter-${id}`}
    >
      {label}
    </button>
  );
}

function ParticipantRow({ p, onApprove, onReject, onPromote }) {
  const meta = STATUS_META[p.status] || STATUS_META.confirmed;
  const StatusIcon = meta.icon;
  const initials = (p.user_name || p.user_email || "?")[0].toUpperCase();
  return (
    <div className="card-lux p-4 flex items-center gap-4" data-testid={`participant-${p.id}`}>
      {p.user_picture ? (
        <img src={p.user_picture} alt="" className="w-12 h-12 rounded-xl object-cover border border-[#EADDC9] flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-[#FAF3EA] flex items-center justify-center font-serif text-lg text-[#A26F3E] border border-[#EADDC9] flex-shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-[#1A1A1A] truncate">{p.user_name || p.user_email}</p>
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            <StatusIcon className="w-3 h-3" /> {meta.label}
          </span>
          {p.checked_in_at && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Présent
            </span>
          )}
        </div>
        <p className="text-xs text-[#8B8680] mt-0.5">
          {p.user_email} · {p.user_role && <span className="capitalize">{p.user_role}</span>} · {new Date(p.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {p.status === "pending" && (
          <>
            <Button size="sm" onClick={onApprove} className="rounded-full bg-[#7A8E3F] hover:bg-[#5C6E2D] text-white text-xs h-8 px-3" data-testid={`approve-${p.id}`}>
              <Check className="w-3 h-3 mr-1" /> Approuver
            </Button>
            <Button size="sm" variant="outline" onClick={onReject} className="rounded-full text-xs h-8 px-3 border-[#EADDC9] text-[#B44F3F]" data-testid={`reject-${p.id}`}>
              <X className="w-3 h-3 mr-1" /> Refuser
            </Button>
          </>
        )}
        {p.status === "waitlist" && (
          <Button size="sm" onClick={onPromote} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-xs h-8 px-3" data-testid={`promote-${p.id}`}>
            <ArrowUp className="w-3 h-3 mr-1" /> Promouvoir
          </Button>
        )}
      </div>
    </div>
  );
}
